import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Send, PenLine, Star, Loader2, Sparkles, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import StatusPill from "@/components/marketplace/StatusPill";
import EstimateForm from "@/components/servicerequest/EstimateForm";
import { SR_STYLES, isVisible } from "@/components/servicerequest/QuestionField";
import { budgetLabel } from "@/components/servicerequest/ReviewStep";

function ProviderPicker({ requestId, canEdit, open, kind, onChanged }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [writing, setWriting] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get(`/admin/service-requests/requests/${requestId}/providers`, { params: { q: q || undefined } }).then((r) => setItems(r.data.items)), [requestId, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const assign = async (p) => {
    setBusy(p.id);
    try { await api.post(`/admin/service-requests/requests/${requestId}/assign`, { provider_id: p.id }); toast.success(`Job sent to ${p.name}`); load(); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.error || "Could not send"); } finally { setBusy(null); }
  };
  if (writing) {
    return (
      <div className="space-y-3">
        <button onClick={() => setWriting(null)} className="text-sm text-[#165a71]" data-testid="asr-cancel-estimate">← Back to providers</button>
        <EstimateForm kind={kind} title={`Estimate from ${writing.name}`} submitUrl={`/admin/service-requests/requests/${requestId}/estimate`} extraBody={{ provider_id: writing.id }} onDone={() => { setWriting(null); load(); onChanged(); }} />
      </div>
    );
  }
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="asr-provider-picker">
      <div className="font-display text-lg font-semibold text-[#071b22]">Select a provider for this customer</div>
      <p className="text-xs text-[#252A34]/60 mt-1">Best matches are listed first. Search to find any provider.</p>
      <div className="relative mt-3"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search providers by name" className="sr-input !pl-9" data-testid="asr-provider-search" /></div>
      {!open && <div className="mt-3 rounded-xl bg-amber-50 text-amber-800 text-xs px-3 py-2">This request is no longer open for estimates.</div>}
      <div className="mt-3 divide-y divide-slate-100 max-h-[520px] overflow-auto">
        {items.map((p) => {
          const has = p.opportunity_status;
          const quoted = ["estimated", "won"].includes(has);
          return (
            <div key={p.id} className="py-3 flex items-center gap-3" data-testid={`asr-provider-${p.id}`}>
              <div className="h-9 w-9 rounded-lg bg-[#165a71]/10 text-[#165a71] text-xs font-semibold flex items-center justify-center tabular-nums">{Math.round(p.score)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#071b22] truncate flex items-center gap-2">{p.name}
                  {p.slug && <a href={`/providers/${p.slug}`} target="_blank" rel="noreferrer" className="text-[#165a71]"><ExternalLink className="h-3 w-3" /></a>}
                </div>
                <div className="text-[11px] text-[#252A34]/60 flex items-center gap-2 flex-wrap">
                  {p.rating_avg ? <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 fill-[#d9a441] text-[#d9a441]" />{Number(p.rating_avg).toFixed(1)}</span> : null}
                  <span className="truncate">{(p.reasons || []).slice(0, 2).join(" · ")}</span>
                  {has && <span className="rounded-full bg-slate-100 px-2 py-0.5" data-testid={`asr-provider-status-${p.id}`}>{has}</span>}
                </div>
              </div>
              {canEdit && open && (
                <div className="flex gap-1.5">
                  <button disabled={!!has || busy === p.id} onClick={() => assign(p)} title={has ? "Already has this job" : "Send job to this pro to quote"} className="rounded-full border border-[#165a71]/40 text-[#165a71] px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-40" data-testid={`asr-assign-${p.id}`}><Send className="h-3 w-3" /> Send job</button>
                  <button disabled={quoted} onClick={() => setWriting(p)} title={quoted ? "Already has an estimate" : "Write estimate for this pro"} className="rounded-full bg-[#165a71] text-white px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-40" data-testid={`asr-write-estimate-${p.id}`}><PenLine className="h-3 w-3" /> Write estimate</button>
                </div>
              )}
            </div>
          );
        })}
        {!items.length && <div className="py-6 text-center text-sm text-[#252A34]/60">No providers found.</div>}
      </div>
    </div>
  );
}

export default function AdminServiceRequestDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = (user?.roles || []).some((r) => ["administrator", "super_administrator"].includes(r));
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/service-requests/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Could not load request")), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="flex items-center gap-2 text-[#252A34]/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const r = d.request;
  const open = ["published", "receiving_proposals"].includes(r.status);
  const answers = r.answers || {};
  const qa = (r.questionnaire_snapshot?.sections || []).flatMap((s) => s.questions).filter((q) => isVisible(q, answers) && answers[q.id] != null && answers[q.id] !== "");

  return (
    <div className="max-w-7xl" data-testid="admin-service-request-detail">
      <style>{SR_STYLES}</style>
      <Link to="/admin/service-requests" className="text-sm text-[#165a71]">← Service requests</Link>
      <div className="mt-2 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">{r.questionnaire_snapshot?.name}</div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-[#071b22]" data-testid="asr-title">{r.title}</h1>
          <div className="text-xs text-[#252A34]/60 mt-1">{r.address_snapshot?.city ? `${r.address_snapshot.city}, ${r.address_snapshot.state} · ` : ""}{budgetLabel(r)} · {(r.urgency || "").replace(/_/g, " ")}</div>
        </div>
        <div className="flex items-center gap-3"><StatusPill status={r.status} /><Link to={`/dashboard/service-requests/${r.id}`} className="text-xs text-[#165a71] underline" data-testid="asr-customer-view">Customer view</Link></div>
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.1fr] items-start">
        <div className="space-y-5">
          <div className="rounded-3xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#165a71]"><Sparkles className="h-4 w-4" /> Brief</div>
            <p className="mt-2 text-sm text-[#071b22]">{r.ai_summary}</p>
            <div className="mt-3 divide-y divide-slate-100">{qa.map((q) => <div key={q.id} className="py-1.5 flex justify-between gap-4 text-xs"><span className="text-[#252A34]/60">{q.label}</span><span className="font-medium text-right">{Array.isArray(answers[q.id]) ? answers[q.id].join(", ") : String(answers[q.id])}</span></div>)}</div>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="asr-estimates">
            <div className="font-display font-semibold text-[#071b22]">Estimates ({d.estimates.length})</div>
            {d.estimates.map((e) => (
              <div key={e.id} className="mt-3 rounded-xl border border-slate-100 p-3 text-sm flex items-center justify-between gap-3" data-testid={`asr-estimate-${e.id}`}>
                <div className="min-w-0"><div className="font-medium truncate">{e.provider_snapshot?.display_name}</div><div className="text-xs text-[#252A34]/60">{e.status}{e.recommended_by_admin ? " · sent by admin" : ""}</div></div>
                <div className="font-display font-semibold">${Number(e.price).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="asr-opportunities">
            <div className="font-display font-semibold text-[#071b22]">Sent to ({(d.opportunities || []).length})</div>
            {(d.opportunities || []).map((o) => (
              <div key={o.id} className="mt-2 flex items-center justify-between text-xs" data-testid={`asr-opp-${o.id}`}>
                <span className="truncate">{o.provider_name || o.provider_id}{o.admin_assigned ? " · by admin" : ` · round ${o.wave}`}</span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5">{o.status}</span>
              </div>
            ))}
          </div>
        </div>
        <ProviderPicker requestId={r.id} canEdit={canEdit} open={open} kind={r.kind} onChanged={load} />
      </div>
    </div>
  );
}
